import { useEffect, useState } from "react";

const DISMISSED_KEY = "mm_install_nudge_v1";

function isIOSSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isWebKit = /webkit/i.test(ua);
  // Chrome/Firefox/Opera on iOS include these tokens — exclude them so we only
  // target Safari (which is the only iOS browser that supports Add to Home Screen)
  const isOtherBrowser = /crios|fxios|opios|mercury|EdgiOS/i.test(ua);
  return isIOS && isWebKit && !isOtherBrowser;
}

function isAlreadyInstalled(): boolean {
  return (
    (window.navigator as any).standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

export default function InstallBanner() {
  const [visible, setVisible] = useState(false);
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    if (!isIOSSafari()) return;
    if (isAlreadyInstalled()) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;
    // Delay so the app finishes loading before the nudge appears
    const t = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(t);
  }, []);

  function dismiss() {
    setHiding(true);
    localStorage.setItem(DISMISSED_KEY, "1");
    setTimeout(() => setVisible(false), 280);
  }

  if (!visible) return null;

  return (
    <div style={{
      position: "fixed",
      bottom: 0,
      left: "50%",
      transform: "translateX(-50%)",
      width: "100%",
      maxWidth: 480,
      zIndex: 9999,
      padding: "0 0 env(safe-area-inset-bottom, 0px)",
      animation: hiding ? "slideDown 0.28s ease-in forwards" : "slideUp 0.32s cubic-bezier(0.22,1,0.36,1) forwards",
    }}>
      <style>{`
        @keyframes slideUp   { from { transform: translateX(-50%) translateY(100%); opacity: 0; }
                                to   { transform: translateX(-50%) translateY(0);    opacity: 1; } }
        @keyframes slideDown { from { transform: translateX(-50%) translateY(0);    opacity: 1; }
                                to   { transform: translateX(-50%) translateY(100%); opacity: 0; } }
      `}</style>

      <div style={{
        background: "#fff",
        borderTop: "3px solid #0d9488",
        boxShadow: "0 -6px 24px rgba(0,0,0,0.14)",
        padding: "12px 16px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}>
        {/* Coin icon */}
        <div style={{
          width: 40, height: 40, borderRadius: 10, background: "#0d9488",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, fontSize: 20,
        }}>
          🪙
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#1f2937", fontFamily: "Nunito, sans-serif" }}>
            Add MoneyMission to your home screen
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4, fontFamily: "Nunito, sans-serif" }}>
            Tap{" "}
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 1,
              background: "#f3f4f6", border: "1px solid #d1d5db",
              borderRadius: 5, padding: "1px 5px", fontSize: 11, fontWeight: 700, color: "#374151",
            }}>
              <ShareIcon /> Share
            </span>
            {" "}then{" "}
            <strong style={{ color: "#1f2937" }}>"Add to Home Screen"</strong>
          </div>
        </div>

        {/* Dismiss */}
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            background: "#f3f4f6",
            border: "none",
            borderRadius: 50,
            width: 28, height: 28,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0,
            fontSize: 14, color: "#6b7280", fontWeight: 900,
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ display: "inline-block", verticalAlign: "middle" }}>
      <path d="M8 6L12 2L16 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
      <path d="M5 12v8a1 1 0 001 1h12a1 1 0 001-1v-8" />
    </svg>
  );
}
